import type { QueryClient } from '@tanstack/react-query'

import type { TaskWithAssignments } from '@scrambl/shared'
import { TaskStatusTypeEnum } from '@scrambl/shared'

type TaskLike = Omit<
  Partial<TaskWithAssignments>,
  'status' | 'statusTypeId' | 'completedAt'
> & {
  id?: string
  statusTypeId?: string | null
  status?: string | null
  completedAt?: Date | string | null
}

export function isCompletedTaskForSummary(task: TaskLike): boolean {
  return (
    task.statusTypeId === TaskStatusTypeEnum.COMPLETED ||
    !!task.completedAt ||
    task.status === 'COMPLETED' ||
    task.status === 'DONE'
  )
}

export function updateParentSubtaskSummaryCacheData<TData>(
  oldData: TData,
  parentTaskId: string,
  deltas: { subtaskDelta?: number; completedDelta?: number },
): TData {
  const subtaskDelta = deltas.subtaskDelta ?? 0
  const completedDelta = deltas.completedDelta ?? 0

  if (!oldData || (subtaskDelta === 0 && completedDelta === 0)) {
    return oldData
  }

  const updateTask = <TTask extends TaskLike>(task: TTask): TTask => {
    if (task.id !== parentTaskId) {
      return task
    }

    return {
      ...task,
      subtaskCount: Math.max(0, (task.subtaskCount || 0) + subtaskDelta),
      completedSubtaskCount: Math.max(
        0,
        (task.completedSubtaskCount || 0) + completedDelta,
      ),
    }
  }

  if (Array.isArray(oldData)) {
    return oldData.map(task => updateTask(task as TaskLike)) as TData
  }

  if (typeof oldData !== 'object') {
    return oldData
  }

  const record = oldData as Record<string, unknown>

  if (record.id === parentTaskId) {
    return updateTask(record as TaskLike) as TData
  }

  if (Array.isArray(record.tasks)) {
    return {
      ...record,
      tasks: record.tasks.map(task => updateTask(task as TaskLike)),
    } as TData
  }

  if (Array.isArray(record.data)) {
    return {
      ...record,
      data: record.data.map(task => updateTask(task as TaskLike)),
    } as TData
  }

  return oldData
}

export function updateParentSubtaskSummaryQueries(
  queryClient: QueryClient,
  parentTaskId: string | undefined,
  deltas: { subtaskDelta?: number; completedDelta?: number },
) {
  if (!parentTaskId) {
    return
  }

  queryClient.setQueryData(['task', parentTaskId], oldData =>
    updateParentSubtaskSummaryCacheData(oldData, parentTaskId, deltas),
  )

  queryClient.setQueriesData(
    {
      predicate: query => {
        const key = query.queryKey
        if (!Array.isArray(key)) {
          return false
        }

        if (key[0] === 'my-tasks') {
          return true
        }

        return (
          key[0] === 'tasks' &&
          key[1] !== 'groups' &&
          key[1] !== 'groups-with-counts' &&
          key[1] !== 'status-counts'
        )
      },
    },
    oldData =>
      updateParentSubtaskSummaryCacheData(oldData, parentTaskId, deltas),
  )
}
